import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Book, ArrowLeft, MapPin, Calendar, Trophy, Quotes, Sparkle,
  BookOpen, Feather, Scroll, ImageSquare, Heart, Eye, CaretDown, CaretUp, Translate,
  MagnifyingGlass, ArrowRight, Clock, ChatCircleDots
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import WorkReviews from '../components/WorkReviews';
import AddToCollectionButton from '../components/AddToCollectionButton';
import AuthorChatDrawer from '../components/AuthorChatDrawer';
import { proxyImage } from '../lib/imageProxy';
import GlobalHeader from '../components/GlobalHeader';
import { createSafeRichHTML } from '../lib/sanitize';

const WORK_TYPE_LABELS = {
  poem: 'Poems',
  poetry: 'Poetry',
  story: 'Stories',
  short_story: 'Short Stories',
  novel: 'Novels',
  essay: 'Essays',
  play: 'Plays',
  memoir: 'Memoirs',
  criticism: 'Criticism',
  epic: 'Epics',
  doha: 'Dohas',
  bhajan: 'Bhajans',
  ghazal: 'Ghazals'
};

const WORK_TYPE_SINGULAR = {
  poem: 'Poem', poetry: 'Poem', story: 'Story', short_story: 'Short Story',
  novel: 'Novel', essay: 'Essay', play: 'Play', memoir: 'Memoir',
  criticism: 'Criticism', epic: 'Epic', doha: 'Doha', bhajan: 'Bhajan', ghazal: 'Ghazal'
};

const workLabel = (t) => WORK_TYPE_LABELS[t?.toLowerCase()] || (t ? t.charAt(0).toUpperCase() + t.slice(1) + 's' : 'Works');
const workSingular = (t) => WORK_TYPE_SINGULAR[t?.toLowerCase()] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Work');

export const AuthorDetail = () => {
  const { authorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [author, setAuthor] = useState(null);
  const [related, setRelated] = useState([]);
  const [works, setWorks] = useState([]);
  const [summary, setSummary] = useState({ total_works: 0, categories: [] });
  const [loading, setLoading] = useState(true);
  const [worksLoading, setWorksLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [analysis, setAnalysis] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [bioSummary, setBioSummary] = useState('');
  const [summarizing, setSummarizing] = useState(false);
  const [expandedWork, setExpandedWork] = useState(null);
  const [explaining, setExplaining] = useState(null);
  const [explanations, setExplanations] = useState({});
  const [worksSearch, setWorksSearch] = useState('');
  const [worksSort, setWorksSort] = useState('latest'); // latest, oldest, most_read, most_liked, alphabetical
  const [activeCategory, setActiveCategory] = useState(''); // '' = all

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        setLoading(true);
        const [authorRes, relatedRes, summaryRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors/${authorId}`),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors/${authorId}/related`),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors/${authorId}/works/summary`).catch(() => ({ data: { total_works: 0, categories: [] } }))
        ]);
        setAuthor(authorRes.data);
        setRelated(relatedRes.data);
        setSummary(summaryRes.data);
        if (authorRes.data?.ai_bio_summary) setBioSummary(authorRes.data.ai_bio_summary);
        if (authorRes.data?.ai_style_analysis) setAnalysis(authorRes.data.ai_style_analysis);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, [authorId]);

  useEffect(() => {
    // Debounced re-fetch of works whenever filters change
    const t = setTimeout(async () => {
      if (!authorId) return;
      try {
        setWorksLoading(true);
        const params = new URLSearchParams({ sort: worksSort });
        if (worksSearch.trim()) params.set('search', worksSearch.trim());
        if (activeCategory) params.set('work_type', activeCategory);
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors/${authorId}/works?${params.toString()}`);
        setWorks(res.data || []);
      } catch (e) {
        setWorks([]);
      } finally {
        setWorksLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [authorId, worksSearch, worksSort, activeCategory]);

  const timelineEvents = useMemo(() => {
    if (!author) return [];
    const evts = [];
    const parseYear = (d) => {
      if (!d) return null;
      const m = String(d).match(/\d{4}/);
      return m ? parseInt(m[0], 10) : null;
    };
    const birthYear = parseYear(author.birth_date);
    const deathYear = parseYear(author.death_date);
    if (birthYear) evts.push({ year: birthYear, title: 'Born', detail: author.birth_place || '' });
    works.forEach((w) => {
      const y = parseYear(w.year);
      if (y) evts.push({ year: y, title: w.title, detail: workSingular(w.work_type) });
    });
    if (deathYear) evts.push({ year: deathYear, title: 'Passed away', detail: '' });
    return evts.sort((a, b) => a.year - b.year);
  }, [author, works]);

  const handleGenerateSummary = async () => {
    if (!user) return toast.error('Please sign in to use AI features');
    try {
      setSummarizing(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/author-bio-summary`,
        { text: `Author: ${author.name}. ${author.biography}`, target_lang: author.slug },
        { withCredentials: true, timeout: 60000 }
      );
      setBioSummary(res.data.summary);
      toast.success(res.data.cached ? 'Loaded cached summary' : 'Summary generated');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`AI failed: ${msg}`);
      console.error('bio-summary error:', e);
    } finally {
      setSummarizing(false);
    }
  };

  const handleAIAnalysis = async () => {
    if (!user) return toast.error('Please sign in to use AI features');
    try {
      setAnalyzing(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/author-analysis`,
        {
          text: `Author: ${author.name}. Bio: ${author.biography}. Themes: ${(author.themes || []).join(', ')}. Style: ${author.writing_style || ''}`,
          target_lang: author.slug
        },
        { withCredentials: true, timeout: 60000 }
      );
      setAnalysis(res.data.analysis);
      toast.success(res.data.cached ? 'Loaded cached analysis' : 'Analysis generated');
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`AI failed: ${msg}`);
      console.error('author-analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleExplainWork = async (work) => {
    if (!user) return toast.error('Please sign in to use AI features');
    if (explanations[work.work_id]) {
      setExplanations({ ...explanations, [work.work_id]: null });
      return;
    }
    try {
      setExplaining(work.work_id);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/explain-hindi`,
        { text: work.content },
        { withCredentials: true }
      );
      setExplanations({ ...explanations, [work.work_id]: res.data.explanation });
    } catch (e) {
      toast.error('Failed to explain');
    } finally {
      setExplaining(null);
    }
  };

  const handleLikeWork = async (work) => {
    if (!user) return toast.error('Please sign in to like');
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/authors/${author.author_id}/works/${work.work_id}/like`,
        {}, { withCredentials: true }
      );
      const updated = works.map((w) => w.work_id === work.work_id ? { ...w, likes: (w.likes || 0) + 1 } : w);
      setWorks(updated);
    } catch (e) {
      toast.error('Failed to like');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Author not found</p>
          <Link to="/authors" className="text-primary hover:underline">Browse authors</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      {/* Cover Image Hero */}
      <div className="relative h-64 md:h-96 overflow-hidden">
        {author.cover_image ? (
          <img src={proxyImage(author.cover_image)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-background"></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
      </div>

      <div className="container mx-auto px-6 -mt-32 relative z-10 pb-16">
        <div className="max-w-5xl mx-auto">
          <Link to="/authors" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full">
            <ArrowLeft size={16} />
            All Authors
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Photograph & Quick Facts */}
            <div className="md:col-span-1">
              <div className="aspect-[3/4] bg-card border border-border rounded-lg overflow-hidden shadow-lg">
                {author.photograph ? (
                  <img src={proxyImage(author.photograph)} alt={author.name} className="w-full h-full object-cover" data-testid="author-photo" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Feather size={64} weight="duotone" className="text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Quick Facts */}
              <div className="mt-4 bg-card border border-border rounded-lg p-5 space-y-3 text-sm" data-testid="quick-facts">
                {author.birth_date && (
                  <div className="flex items-start gap-2">
                    <Calendar size={16} className="text-primary mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Lifespan</div>
                      <div>{author.birth_date}{author.death_date ? ` – ${author.death_date}` : ' (Living)'}</div>
                    </div>
                  </div>
                )}
                {author.birth_place && (
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="text-primary mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Birthplace</div>
                      <div>{author.birth_place}</div>
                    </div>
                  </div>
                )}
                {author.languages?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Translate size={16} className="text-primary mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Languages</div>
                      <div>{author.languages.join(', ')}</div>
                    </div>
                  </div>
                )}
                {works.length > 0 && (
                  <div className="flex items-start gap-2">
                    <BookOpen size={16} className="text-primary mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Works</div>
                      <div>{works.length} on Kavyalekh</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="md:col-span-2 space-y-8">
              <div>
                <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-2 block">
                  {author.literary_movement || 'Hindi Literature'}
                </span>
                <h1 className="text-5xl md:text-6xl mb-2 tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }} data-testid="author-name">
                  {author.name}
                </h1>
                {author.pen_name && author.pen_name !== author.name && (
                  <p className="text-muted-foreground italic mb-4">"{author.pen_name}"</p>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  {author.occupation?.map((occ, i) => (
                    <span key={i} className="text-xs bg-muted px-3 py-1 rounded-full">{occ}</span>
                  ))}
                </div>

                {/* Claude-powered Chat CTA */}
                {user && (
                  <button
                    onClick={() => setChatOpen(true)}
                    data-testid="open-author-chat-button"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-full py-2.5 px-5 text-sm font-semibold hover:scale-[1.02] hover:opacity-95 transition-transform shadow-sm"
                  >
                    <ChatCircleDots size={16} weight="duotone" />
                    Chat with {author.name.split(' ')[0]}
                    <span className="text-[10px] bg-primary-foreground/20 rounded-full px-2 py-0.5 ml-1">AI</span>
                  </button>
                )}
              </div>

              {/* AI Bio Summary Quick Card */}
              <section className="bg-primary/5 border border-primary/20 rounded-lg p-5" data-testid="ai-bio-summary">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkle size={20} weight="duotone" className="text-primary" />
                    <h3 className="text-lg font-semibold">In a Nutshell</h3>
                  </div>
                  {!bioSummary && (
                    <button
                      onClick={handleGenerateSummary}
                      disabled={summarizing}
                      data-testid="generate-bio-summary-button"
                      className="text-xs bg-primary text-primary-foreground rounded-full py-1.5 px-4 font-semibold hover:opacity-90 disabled:opacity-50"
                    >
                      {summarizing ? 'Generating…' : 'AI Summary'}
                    </button>
                  )}
                </div>
                {bioSummary ? (
                  <p className="text-base leading-relaxed" data-testid="bio-summary-content">{bioSummary}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Get a 2-3 sentence AI summary of this author's significance.</p>
                )}
              </section>

              {/* Full Biography */}
              <section>
                <h2 className="text-3xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                  Biography
                </h2>
                <p className="text-lg leading-relaxed text-foreground/90 whitespace-pre-line" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="author-biography">
                  {author.biography}
                </p>
              </section>

              {/* Timeline */}
              {timelineEvents.length > 0 && (
                <section data-testid="timeline-section">
                  <h2 className="text-3xl mb-6 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                    <Scroll size={24} weight="duotone" className="text-primary" />
                    Life & Works Timeline
                  </h2>
                  <div className="relative border-l-2 border-primary/30 ml-2 pl-6 space-y-4">
                    {timelineEvents.map((e, i) => (
                      <div key={i} className="relative" data-testid={`timeline-event-${i}`}>
                        <div className="absolute -left-[30px] top-1 w-4 h-4 rounded-full bg-primary border-4 border-background"></div>
                        <div className="text-xs font-semibold text-primary uppercase tracking-widest">{e.year}</div>
                        <div className="font-medium">{e.title}</div>
                        {e.detail && <div className="text-sm text-muted-foreground">{e.detail}</div>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Categorized Works */}
              {summary.total_works > 0 && (
                <section data-testid="works-section">
                  <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <h2 className="text-3xl mb-1 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                        <BookOpen size={24} weight="duotone" className="text-primary" />
                        Bibliography
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {summary.total_works} work{summary.total_works !== 1 ? 's' : ''} · {summary.total_views} reads · {summary.total_likes} likes
                      </p>
                    </div>
                  </div>

                  {/* Category chips */}
                  <div className="flex flex-wrap gap-2 mb-4" data-testid="category-chips">
                    <button
                      onClick={() => setActiveCategory('')}
                      data-testid="category-chip-all"
                      className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full font-semibold transition-colors ${!activeCategory ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary'}`}
                    >
                      All ({summary.total_works})
                    </button>
                    {summary.categories.map((c) => (
                      <button
                        key={c.work_type}
                        onClick={() => setActiveCategory(c.work_type === activeCategory ? '' : c.work_type)}
                        data-testid={`category-chip-${c.work_type}`}
                        className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full font-semibold transition-colors ${activeCategory === c.work_type ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary'}`}
                      >
                        {workLabel(c.work_type)} ({c.count})
                      </button>
                    ))}
                  </div>

                  {/* Sort + Search */}
                  <div className="flex gap-2 mb-4">
                    <div className="flex-1 relative">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={worksSearch}
                        onChange={(e) => setWorksSearch(e.target.value)}
                        placeholder="Search works..."
                        data-testid="works-search-input"
                        className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-full outline-none focus:border-primary"
                      />
                    </div>
                    <select
                      value={worksSort}
                      onChange={(e) => setWorksSort(e.target.value)}
                      data-testid="works-sort-select"
                      className="text-sm px-3 py-2 bg-card border border-border rounded-full outline-none focus:border-primary"
                    >
                      <option value="latest">Latest</option>
                      <option value="oldest">Oldest</option>
                      <option value="most_read">Most Read</option>
                      <option value="most_liked">Most Liked</option>
                      <option value="alphabetical">A → Z</option>
                    </select>
                  </div>

                  {/* Works list */}
                  {worksLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : works.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-10 text-center" data-testid="no-works">
                      <p className="text-muted-foreground text-sm">No works match this filter.</p>
                    </div>
                  ) : (
                    <div className="bg-card border border-border rounded-lg overflow-hidden divide-y divide-border">
                      {works.map((work) => {
                        const isExpanded = expandedWork === work.work_id;
                        return (
                          <div key={work.work_id} className="p-5" data-testid={`work-${work.work_id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                  <button
                                    onClick={() => setExpandedWork(isExpanded ? null : work.work_id)}
                                    className="text-left font-semibold text-xl hover:text-primary transition-colors"
                                    data-testid={`work-title-${work.work_id}`}
                                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                                  >
                                    {work.title}
                                  </button>
                                  <span className="text-xs uppercase tracking-widest text-primary/70">{workSingular(work.work_type)}</span>
                                  {work.year && <span className="text-xs text-muted-foreground">· {work.year}</span>}
                                </div>
                                {work.description && (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{work.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                                <span className="flex items-center gap-1" title="Reads"><Eye size={14} /> {work.views || 0}</span>
                                <button
                                  onClick={() => handleLikeWork(work)}
                                  data-testid={`work-like-${work.work_id}`}
                                  className="flex items-center gap-1 hover:text-primary"
                                >
                                  <Heart size={14} /> {work.likes || 0}
                                </button>
                                {work.reading_time > 0 && (
                                  <span className="hidden md:flex items-center gap-1" title="Reading time"><Clock size={14} /> {work.reading_time} min</span>
                                )}
                              </div>
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <button
                                onClick={() => navigate(`/work/${work.work_id}`)}
                                data-testid={`work-read-online-${work.work_id}`}
                                className="text-xs bg-primary text-primary-foreground rounded-full py-1.5 px-4 font-semibold hover:opacity-90 inline-flex items-center gap-1"
                              >
                                <BookOpen size={14} /> Read Online <ArrowRight size={12} />
                              </button>
                              <button
                                onClick={() => setExpandedWork(isExpanded ? null : work.work_id)}
                                data-testid={`work-preview-${work.work_id}`}
                                className="text-xs bg-primary/10 text-primary rounded-full py-1.5 px-4 font-semibold hover:bg-primary/20"
                              >
                                {isExpanded ? 'Hide preview' : 'Quick preview'}
                              </button>
                              {work.is_public_domain && work.downloadable && (
                                <>
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/works/${work.work_id}/download.pdf`}
                                    data-testid={`work-download-pdf-${work.work_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs bg-muted rounded-full py-1.5 px-3 font-semibold hover:bg-muted/80 inline-flex items-center gap-1"
                                  >
                                    ↓ PDF
                                  </a>
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/works/${work.work_id}/download.txt`}
                                    data-testid={`work-download-txt-${work.work_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs bg-muted rounded-full py-1.5 px-3 font-semibold hover:bg-muted/80"
                                  >
                                    ↓ TXT
                                  </a>
                                </>
                              )}
                              <div className="ml-auto">
                                <AddToCollectionButton itemType="work" itemId={work.work_id} />
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="mt-4 space-y-3">
                                <div
                                  className="prose prose-sm max-w-none text-base leading-relaxed bg-muted/30 border border-border rounded p-4 whitespace-pre-line"
                                  style={{ fontFamily: 'Mukta, IBM Plex Sans, sans-serif' }}
                                  data-testid={`work-content-${work.work_id}`}
                                  dangerouslySetInnerHTML={createSafeRichHTML(work.content)}
                                />
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => handleExplainWork(work)}
                                    disabled={explaining === work.work_id}
                                    data-testid={`work-explain-${work.work_id}`}
                                    className="text-xs bg-primary/10 text-primary rounded-full py-1.5 px-3 font-semibold hover:bg-primary/20 disabled:opacity-50 inline-flex items-center gap-1"
                                  >
                                    <Sparkle size={14} />
                                    {explaining === work.work_id ? 'Explaining…' : (explanations[work.work_id] ? 'Hide Explanation' : 'Explain in Simple Words')}
                                  </button>
                                </div>
                                {explanations[work.work_id] && (
                                  <div className="bg-primary/5 border-l-2 border-primary p-3 text-sm whitespace-pre-line" data-testid={`work-explanation-${work.work_id}`}>
                                    {explanations[work.work_id]}
                                  </div>
                                )}

                                {/* Reviews & ratings */}
                                <WorkReviews workId={work.work_id} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {/* AI Literary Style Analysis */}
              <section className="bg-card border border-border rounded-lg p-6" data-testid="ai-analysis-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                    <Sparkle size={24} weight="duotone" className="inline mr-2 text-primary" />
                    AI Literary Analysis
                  </h3>
                  {!analysis && (
                    <button
                      onClick={handleAIAnalysis}
                      disabled={analyzing}
                      data-testid="ai-analysis-button"
                      className="bg-primary text-primary-foreground rounded-full py-2 px-5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {analyzing ? 'Analyzing...' : 'Generate Analysis'}
                    </button>
                  )}
                </div>
                {analysis ? (
                  <p className="text-base leading-relaxed whitespace-pre-line" data-testid="ai-analysis-content">{analysis}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Click to get AI-powered insights into this author's literary style, themes, and contributions.</p>
                )}
              </section>

              {/* Literary Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {author.genres?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Genres</h4>
                    <div className="flex flex-wrap gap-2">
                      {author.genres.map((g, i) => (
                        <span key={i} className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">{g}</span>
                      ))}
                    </div>
                  </div>
                )}
                {author.themes?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Themes</h4>
                    <div className="flex flex-wrap gap-2">
                      {author.themes.map((t, i) => (
                        <span key={i} className="text-sm bg-muted px-3 py-1 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {author.writing_style && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Writing Style</h4>
                    <p className="text-sm">{author.writing_style}</p>
                  </div>
                )}
                {author.influences?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Influenced</h4>
                    <p className="text-sm">{author.influences.join(', ')}</p>
                  </div>
                )}
                {author.influenced_by?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Influenced by</h4>
                    <p className="text-sm">{author.influenced_by.join(', ')}</p>
                  </div>
                )}
              </div>

              {/* Media Gallery */}
              {(author.photograph || author.cover_image) && (
                <section data-testid="media-gallery">
                  <h3 className="text-2xl mb-4 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                    <ImageSquare size={24} weight="duotone" className="text-primary" />
                    Media Gallery
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {author.photograph && (
                      <a href={proxyImage(author.photograph)} target="_blank" rel="noreferrer" className="aspect-square bg-muted rounded-lg overflow-hidden border border-border hover:border-primary transition-colors">
                        <img src={proxyImage(author.photograph)} alt={author.name} className="w-full h-full object-cover" />
                      </a>
                    )}
                    {author.cover_image && author.cover_image !== author.photograph && (
                      <a href={proxyImage(author.cover_image)} target="_blank" rel="noreferrer" className="aspect-square bg-muted rounded-lg overflow-hidden border border-border hover:border-primary transition-colors">
                        <img src={proxyImage(author.cover_image)} alt="" className="w-full h-full object-cover" />
                      </a>
                    )}
                  </div>
                </section>
              )}

              {/* Awards */}
              {author.awards?.length > 0 && (
                <section className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                  <h3 className="text-2xl mb-4 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                    <Trophy size={24} weight="duotone" className="text-primary" />
                    Awards & Honors
                  </h3>
                  <ul className="space-y-2">
                    {author.awards.map((award, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span>{award}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Famous Quotes */}
              {author.famous_quotes?.length > 0 && (
                <section>
                  <h3 className="text-2xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                    <Quotes size={24} weight="duotone" className="inline mr-2 text-primary" />
                    Memorable Quotes
                  </h3>
                  <div className="space-y-4">
                    {author.famous_quotes.map((quote, i) => (
                      <blockquote
                        key={i}
                        className="bg-card border-l-4 border-primary p-6 rounded-r-lg text-lg italic"
                        style={{ fontFamily: 'IBM Plex Sans, Mukta, sans-serif' }}
                        data-testid={`quote-${i}`}
                      >
                        "{quote}"
                      </blockquote>
                    ))}
                  </div>
                </section>
              )}

              {/* Related Authors */}
              {related.length > 0 && (
                <section data-testid="related-authors-section">
                  <h3 className="text-2xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                    Related Authors
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {related.map((r) => (
                      <Link
                        key={r.author_id}
                        to={`/author/${r.slug}`}
                        data-testid={`related-author-${r.author_id}`}
                        className="bg-card border border-border rounded-lg p-4 hover:border-primary transition-colors text-center"
                      >
                        {r.photograph ? (
                          <img src={proxyImage(r.photograph)} alt={r.name} className="w-20 h-20 rounded-full mx-auto mb-2 object-cover" />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-muted mx-auto mb-2 flex items-center justify-center">
                            <Feather size={24} weight="duotone" className="text-muted-foreground" />
                          </div>
                        )}
                        <p className="text-sm font-semibold">{r.name}</p>
                        {r.literary_movement && <p className="text-xs text-muted-foreground">{r.literary_movement}</p>}
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      <AuthorChatDrawer author={author} open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
};

export default AuthorDetail;
